import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, PhotoIcon, EyeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const HeroImagesPage = () => {
  const [heroImages, setHeroImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    buttonText: '',
    buttonLink: '',
    displayOrder: 1,
    isActive: false
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    fetchHeroImages();
  }, []);



  const fetchHeroImages = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/hero-images`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHeroImages(data);
      } else {
        toast.error('Failed to fetch hero images');
      }
    } catch (error) {
      console.error('Error fetching hero images:', error);
      toast.error('Error loading hero images');
    } finally {
      setLoading(false);
    }
  };



  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      buttonText: '',
      buttonLink: '',
      displayOrder: 1,
      isActive: false
    });
    setSelectedFile(null);
    setPreviewUrl('');
    setEditingImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    if (!selectedFile && !editingImage) {
      toast.error('Image is required');
      return;
    }
    
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      
      if (selectedFile) {
        submitData.append('image', selectedFile);
      }

      const url = editingImage 
        ? `${API_BASE_URL}/hero-images/${editingImage.id}`
        : `${API_BASE_URL}/hero-images`;
      
      const method = editingImage ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        credentials: 'include',
        body: submitData
      });

      if (response.ok) {
        toast.success(`Hero image ${editingImage ? 'updated' : 'created'} successfully`);
        setShowModal(false);
        resetForm();
        fetchHeroImages();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || errorData.message || 'Failed to save hero image');
      }
    } catch (error) {
      console.error('Error saving hero image:', error);
      toast.error('Error saving hero image');
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      title: image.title || '',
      subtitle: image.subtitle || '',
      buttonText: image.buttonText || '',
      buttonLink: image.buttonLink || '',
      displayOrder: image.displayOrder || 1,
      isActive: image.isActive || false
    });
    // Use signedUrl if available, otherwise fall back to imageData or imageUrl for backward compatibility
    setPreviewUrl(image.signedUrl || image.imageData || image.imageUrl || '');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this hero image?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/hero-images/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        toast.success('Hero image deleted successfully');
        fetchHeroImages();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete error:', response.status, errorData);
        toast.error(errorData.message || 'Failed to delete hero image');
      }
    } catch (error) {
      console.error('Error deleting hero image:', error);
      toast.error('Error deleting hero image');
    }
  };

  const handleActivate = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/hero-images/${id}/activate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        toast.success('Hero image activated successfully');
        fetchHeroImages();
      } else {
        toast.error('Failed to activate hero image');
      }
    } catch (error) {
      console.error('Error activating hero image:', error);
      toast.error('Error activating hero image');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hero Images</h1>
          <p className="text-gray-600">Manage homepage hero images and banners</p>
          

        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary flex items-center space-x-2"

        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Hero Image</span>
        </button>
      </div>

      {/* Hero Images Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {heroImages.map((image) => (
          <div key={image.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
            {/* Image Preview */}
            <div className="aspect-video bg-gray-100 relative">
              {(image.signedUrl || image.imageData || image.imageUrl) ? (
                <img
                  src={image.signedUrl || image.imageData || (image.imageUrl.startsWith('http') ? image.imageUrl : `${API_BASE_URL.replace('/api', '')}${image.imageUrl}`)}
                  alt={image.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <PhotoIcon className="h-12 w-12 text-gray-400" />
                </div>
              )}
              
              {/* Active Badge */}
              {image.isActive && (
                <div className="absolute top-2 right-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                    Active
                  </span>
                </div>
              )}
            </div>
            
            {/* Content */}
            <div className="p-4">
              <h3 className="font-medium text-gray-900 mb-1">
                {image.title || 'Untitled'}
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {image.subtitle || 'No subtitle'}
              </p>
              
              <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                <span>Order: {image.displayOrder}</span>
                <span>Created: {new Date(image.createdAt).toLocaleDateString()}</span>
              </div>
              
              {/* Actions */}
              <div className="flex space-x-2">
                {!image.isActive && (
                  <button
                    onClick={() => handleActivate(image.id)}
                    className="btn-sm btn-secondary flex-1"
                    title="Activate"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    Activate
                  </button>
                )}
                <button
                  onClick={() => handleEdit(image)}
                  className="btn-sm btn-outline"
                  title="Edit"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(image.id)}
                  className="btn-sm btn-danger"
                  title="Delete"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {heroImages.length === 0 && (
        <div className="text-center py-12">
          <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hero images</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating your first hero image.</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editingImage ? 'Edit Hero Image' : 'Add Hero Image'}
              </h2>
              

              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hero Image
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    {previewUrl ? (
                      <div className="relative">
                        <img
                          src={previewUrl}
                          alt="Preview"
                          className="w-full h-48 object-cover rounded"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setPreviewUrl('');
                            setSelectedFile(null);
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-center">
                        <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-2">
                          <label className="cursor-pointer">
                            <span className="text-sm text-gray-600">
                              Click to upload or drag and drop
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleFileChange}
                              className="hidden"
                            />
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="Enter hero title"
                  />
                </div>

                {/* Subtitle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subtitle
                  </label>
                  <textarea
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleInputChange}
                    rows={3}
                    className="input-field"
                    placeholder="Enter hero subtitle or description"
                  />
                </div>

                {/* Button Text and Link */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Button Text
                    </label>
                    <input
                      type="text"
                      name="buttonText"
                      value={formData.buttonText}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="e.g., Learn More"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Button Link
                    </label>
                    <input
                      type="text"
                      name="buttonLink"
                      value={formData.buttonLink}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="e.g., /sports or https://example.com"
                    />
                  </div>
                </div>

                {/* Display Order and Active Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Order
                    </label>
                    <input
                      type="number"
                      name="displayOrder"
                      value={formData.displayOrder}
                      onChange={handleInputChange}
                      min="1"
                      className="input-field"
                    />
                  </div>
                  <div className="flex items-center pt-6">
                    <input
                      type="checkbox"
                      name="isActive"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                      Set as active hero image
                    </label>
                  </div>
                </div>



                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    <span>{editingImage ? 'Update' : 'Save'} Hero Image</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroImagesPage;